<template>
  <v-dialog v-model="studentGroupsStore.modalOpen" fullscreen scrim="white" no-click-animation class="student-groups-dialog">
    <div class="student-groups-overlay" data-testid="StudentGroupsModal-overlay">
      <div class="student-groups-card">
        <div class="student-groups-toolbar-row">
          <v-select
            v-model="selectedJournalId"
            variant="outlined"
            placeholder="Izvēlēties žurnālu"
            menu-icon="fa-solid fa-chevron-down"
            hide-details
            :items="journalOptions"
            :loading="loadingJournals"
            class="student-groups-journal-select"
            data-testid="StudentGroupsModal-journalSelect"
          />

          <v-btn
            variant="flat"
            rounded="pill"
            class="student-groups-shuffle-btn px-4 font-weight-bold"
            color="yellowBack"
            :disabled="participantCount === 0"
            data-testid="StudentGroupsModal-shuffle"
            @click="shuffle"
          >
            <v-icon size="14" class="mr-2">fa-light fa-shuffle</v-icon>
            Sajaukt
          </v-btn>
        </div>

        <div class="student-groups-settings">
          <span class="student-groups-settings-label">Grupu skaits</span>
          <div class="student-groups-stepper">
            <button type="button" data-testid="StudentGroupsModal-fewerGroups" aria-label="Mazāk grupu" @click="changeGroupCount(-1)">−</button>
            <span data-testid="StudentGroupsModal-groupCount">{{ groupCount }}</span>
            <button type="button" data-testid="StudentGroupsModal-moreGroups" aria-label="Vairāk grupu" @click="changeGroupCount(1)">+</button>
          </div>
          <span class="student-groups-settings-divider" />
          <span class="student-groups-count" data-testid="StudentGroupsModal-studentCount">{{ participantCount }} skolēni</span>
        </div>

        <draggable
          v-if="pool.length > 0"
          :list="pool"
          item-key="id"
          group="students"
          class="student-groups-pool"
          data-testid="StudentGroupsModal-pool"
        >
          <template #item="{ element }">
            <div class="student-groups-chip">{{ element.label }}</div>
          </template>
        </draggable>

        <div v-if="groups.length > 0" class="student-groups-grid" data-testid="StudentGroupsModal-groups">
          <div
            v-for="(group, index) in groups"
            :key="index"
            class="student-groups-group"
            :style="{ backgroundColor: groupColor(index).background, borderColor: groupColor(index).border }"
            data-testid="StudentGroupsModal-group"
          >
            <div class="student-groups-group-header">
              <span class="student-groups-group-title" :style="{ color: groupColor(index).title }">{{ index + 1 }}. grupa</span>
              <span class="student-groups-group-size">({{ group.length }})</span>
            </div>
            <draggable :list="group" item-key="id" group="students" class="student-groups-group-members">
              <template #item="{ element }">
                <div class="student-groups-chip">{{ element.label }}</div>
              </template>
            </draggable>
          </div>
        </div>

        <div class="student-groups-excluded">
          <div class="student-groups-excluded-header">
            <span>Nepiedalās</span>
            <span v-if="excluded.length > 0" class="student-groups-group-size">({{ excluded.length }})</span>
          </div>
          <draggable
            :list="excluded"
            item-key="id"
            group="students"
            class="student-groups-excluded-zone"
            data-testid="StudentGroupsModal-excluded"
          >
            <template #item="{ element }">
              <div class="student-groups-chip muted">{{ element.label }}</div>
            </template>
            <template #footer>
              <span v-if="excluded.length === 0" class="student-groups-excluded-hint">Ievelciet šeit, lai izslēgtu no grupām</span>
            </template>
          </draggable>
        </div>
      </div>
    </div>
  </v-dialog>
</template>

<script lang="ts">
import { useTeacherToolsScrimClip } from "~/composables/useTeacherToolsScrimClip"
import { useEklaseStore } from "~/stores/eklaseStore"
import { useStudentGroupsStore } from "~/stores/studentGroupsStore"

interface GroupStudent {
  id: number
  label: string
}

export default {
  setup() {
    const eklaseStore = useEklaseStore()
    const studentGroupsStore = useStudentGroupsStore()
    const scrimClip = useTeacherToolsScrimClip(".student-groups-dialog")
    return { eklaseStore, studentGroupsStore, ...scrimClip }
  },

  data() {
    return {
      selectedJournalId: null as number | null,
      loadingJournals: true,
      groupCount: 6,
      pool: [] as GroupStudent[],
      groups: [] as GroupStudent[][],
      excluded: [] as GroupStudent[],
    }
  },

  computed: {
    journalOptions(): { title: string; value: number }[] {
      return this.eklaseStore.journals.map((journal) => ({
        title: journal.eklase_name,
        value: journal.id,
      }))
    },

    students(): GroupStudent[] {
      const journal = this.eklaseStore.journals.find((j) => j.id === this.selectedJournalId)
      if (!journal) { return [] }
      return journal.students
        .filter((s) => s.active)
        .map((s) => ({ id: s.id, label: `${s.name} ${s.surname}` }))
    },

    participantCount(): number {
      return this.pool.length + this.groups.reduce((sum, group) => sum + group.length, 0)
    },
  },

  watch: {
    "studentGroupsStore.modalOpen"(isOpen: boolean) {
      if (isOpen) {
        this.fetchJournals()
        this.$nextTick(() => this.startScrimClipObserver())
      } else {
        this.stopScrimClipObserver()
        this.reset()
      }
    },

    selectedJournalId() {
      this.resetLists()
    },
  },

  methods: {
    fetchJournals() {
      if (this.eklaseStore.journals.length > 0) {
        this.loadingJournals = false
        return
      }

      this.loadingJournals = true
      this.eklaseStore.fetchJournals({
        successCallback: () => {
          this.loadingJournals = false
        },
        errorCallback: () => {
          this.loadingJournals = false
        },
      })
    },

    resetLists() {
      this.pool = [...this.students]
      this.groups = []
      this.excluded = []
    },

    changeGroupCount(delta: number) {
      this.groupCount = Math.max(this.groupCount + delta, 2)
    },

    groupColor(index: number) {
      const hue = (index * 137.5) % 360
      return {
        background: `hsl(${hue}, 90%, 96%)`,
        border: `hsl(${hue}, 70%, 82%)`,
        title: `hsl(${hue}, 55%, 32%)`,
      }
    },

    shuffle() {
      const participants = [...this.pool, ...this.groups.flat()]
      for (let i = participants.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [participants[i], participants[j]] = [participants[j], participants[i]]
      }

      this.groups = Array.from({ length: this.groupCount }, () => [])
      participants.forEach((student, index) => {
        this.groups[index % this.groupCount].push(student)
      })
      this.pool = []
    },

    reset() {
      this.selectedJournalId = null
      this.groupCount = 6
      this.pool = []
      this.groups = []
      this.excluded = []
    },
  },
}
</script>

<style lang="sass" scoped>
.student-groups-dialog :deep(.v-overlay__scrim)
  background: rgba(255, 255, 255, 0.75) !important
  opacity: 1 !important

  &::before
    content: ""
    position: absolute
    inset: 0
    backdrop-filter: blur(8px)
    z-index: -1

.student-groups-dialog :deep(.v-overlay__content)
  background: transparent !important
  pointer-events: none

.student-groups-overlay
  width: 100vw
  height: 100vh
  display: flex
  align-items: center
  justify-content: center
  pointer-events: none

.student-groups-card
  width: 760px
  max-height: calc(100vh - 100px)
  overflow-y: auto
  background-color: #FFFFFF
  border-radius: 16px
  box-shadow: 0 8px 48px rgba(0, 0, 0, 0.14), 0 0 0 1px rgba(0, 0, 0, 0.06)
  padding: 20px
  display: flex
  flex-direction: column
  gap: 16px
  pointer-events: auto

.student-groups-toolbar-row
  display: flex
  align-items: center
  gap: 8px

.student-groups-journal-select
  flex: 1

.student-groups-shuffle-btn
  height: 44px
  text-transform: none
  font-size: 16px !important
  letter-spacing: 0.1px
  flex-shrink: 0

  &.v-btn--disabled
    background: rgba(0, 0, 0, 0.12) !important
    color: rgba(0, 0, 0, 0.38) !important

.student-groups-settings
  display: flex
  align-items: center
  gap: 12px

.student-groups-settings-label
  font-size: 13px
  font-weight: 500
  color: #6B7280

.student-groups-stepper
  display: flex
  align-items: center
  gap: 2px
  background-color: #F3F4F6
  border-radius: 6px
  padding: 2px 4px

  button
    width: 18px
    height: 18px
    display: flex
    align-items: center
    justify-content: center
    border: none
    background: none
    padding: 0
    color: #6B7280
    font-size: 14px
    cursor: pointer

    &:hover
      color: #1F2937

  span
    font-size: 14px
    font-weight: 600
    color: #374151
    min-width: 22px
    text-align: center
    font-variant-numeric: tabular-nums

.student-groups-settings-divider
  width: 1px
  height: 14px
  background-color: #E5E7EB

.student-groups-count
  margin-left: auto
  font-size: 13px
  color: #9CA3AF

.student-groups-pool
  border: 2px dashed #E5E7EB
  border-radius: 8px
  padding: 8px
  display: flex
  flex-wrap: wrap
  gap: 6px
  min-height: 44px

.student-groups-grid
  display: grid
  grid-template-columns: repeat(3, 1fr)
  gap: 12px

.student-groups-group
  border: 1.5px solid
  border-radius: 8px
  padding: 8px
  display: flex
  flex-direction: column
  gap: 6px
  min-height: 64px

.student-groups-group-header
  display: flex
  align-items: center
  gap: 4px

.student-groups-group-title
  font-size: 11px
  font-weight: 700
  text-transform: uppercase
  letter-spacing: 0.05em

.student-groups-group-size
  font-size: 11px
  color: #9CA3AF

.student-groups-group-members
  display: flex
  flex-wrap: wrap
  gap: 6px
  flex: 1
  min-height: 28px
  align-content: flex-start

.student-groups-chip
  padding: 3px 10px
  border-radius: 999px
  font-size: 13px
  font-weight: 500
  background-color: #FFFFFF
  color: #374151
  border: 1px solid #D1D5DB
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06)
  cursor: grab
  user-select: none

  &:active
    cursor: grabbing

  &.muted
    background-color: #F9FAFB
    color: #9CA3AF
    border-color: #E5E7EB
    box-shadow: none

.student-groups-excluded
  display: flex
  flex-direction: column
  gap: 4px

.student-groups-excluded-header
  display: flex
  align-items: center
  gap: 4px
  font-size: 11px
  font-weight: 500
  color: #9CA3AF
  text-transform: uppercase
  letter-spacing: 0.05em

.student-groups-excluded-zone
  border: 2px dashed #E5E7EB
  border-radius: 8px
  padding: 8px
  display: flex
  flex-wrap: wrap
  gap: 6px
  min-height: 44px

.student-groups-excluded-hint
  font-size: 13px
  color: #C4C8CE
  align-self: center
</style>
